import { Component, inject, Input } from '@angular/core';
import { ListTableComponent } from '../../../shared/components/list-table/list-table.component';
import { TableRowActionsModel } from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import { GridAction } from '../../../shared/components/list-table/grid-action.model';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { throwError } from 'rxjs';
import {
  ProviderHealthAssessment,
  ProviderHealthAssessmentResponseData,
} from '../../../shared/interfaces/lead-details.service-types';
import { GlobalErrorHandlerService } from '../../../shared/services/global-error-handler.service';
import { STATIC_DATA } from '../../../shared/constants/static-data.constants';
import { AccordionComponent } from '../../../shared/components/accordion/accordion.component';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../services/user.service';

const healthAssessmentColumns = [
  {
    field: 'value',
    header: 'Result',
    jsonpath: 'value',
    isDisplayedAtHeader: 'y',
    isSearchable: 'n',
  },
  {
    field: 'start_date',
    header: 'Start Date',
    jsonpath: 'start_date',
    isDisplayedAtHeader: 'y',
    isSearchable: 'n',
  },
  {
    field: 'end_date',
    header: 'Expiration',
    jsonpath: 'end_date',
    isDisplayedAtHeader: 'y',
    isSearchable: 'n',
  },
  {
    field: 'createddate',
    header: 'Created',
    jsonpath: 'createddate',
    isDisplayedAtHeader: 'y',
    isSearchable: 'n',
  },
  {
    field: 'createdbyname',
    header: 'Created By',
    jsonpath: 'createdbyname',
    isDisplayedAtHeader: 'y',
    isSearchable: 'n',
  },
];

const pendingProviderHealthAssessment: ProviderHealthAssessment = {
  description: 'Health Assessment Complete',
  value: 'Pending',
  start_date: null,
  end_date: null,
  is_active: null,
};

@Component({
  selector: 'ppl-health-assessment-list',
  standalone: true,
  imports: [ListTableComponent, AccordionComponent],
  templateUrl: './health-assessment-list.component.html',
  styleUrl: './health-assessment-list.component.css',
})
export class HealthAssessmentListComponent {
  @Input({ required: true }) programCode!: string;

  cols = [...healthAssessmentColumns];
  actionButtons: TableRowActionsModel | null = null;

  selectedProviderId: number | null = null;
  healthAssessmentGridData: ProviderHealthAssessment[] | null = null;

  leadDetailsService = inject(LeadDetailsService);
  errorHandler = inject(GlobalErrorHandlerService);
  userService = inject(UserService);
  providerEmail = '';
  healthAssessmentVendor = '';
  healthAssessmentProgress = '';
  healthAssessments: ProviderHealthAssessment[] | null | undefined = null;
  healthAssessmentDaysRemaining: number | null = null;

  ngOnInit() {
    const storedProviderId: string = JSON.parse(
      this.userService.getSelectedLead()!,
    );
    this.selectedProviderId = storedProviderId ? +storedProviderId : null;
    this.fetchHealthAssessmentData();
  }

  fetchHealthAssessmentData() {
    if (this.selectedProviderId) {
      this.leadDetailsService
        .fetchProviderHealthAssessment(
          this.programCode,
          this.selectedProviderId,
        )
        .subscribe({
          next: (apiResponse) => {
            if (apiResponse?.responsedata?.email) {
              this.providerEmail = apiResponse.responsedata.email;
            }
            if(apiResponse?.responsedata?.vendor) {
              if(apiResponse?.responsedata?.vendor === 'MH') {
                this.healthAssessmentVendor = 'Mobile Health';
              } else if(apiResponse?.responsedata?.vendor === 'IMA') {
                this.healthAssessmentVendor = 'The IMA Group';
              } else {
                this.healthAssessmentVendor = apiResponse.responsedata.vendor;
              }
            }
            if(apiResponse?.responsedata?.mhProgress) {
              this.healthAssessmentProgress = this.toTitleCase(apiResponse.responsedata.mhProgress);
            }
            this.healthAssessments = apiResponse?.responsedata?.assessments;
            this.healthAssessmentDaysRemaining = this.calculateHealthAssessmentDaysRemaining();
            if (apiResponse?.responsedata?.assessments?.length === 0) {
              // No data from Mobile Health, show "Pending" row
              this.setPendingRow();
            } else if (apiResponse?.responsedata?.assessments?.length > 0) {
              // Capitalize the first letter of the 'value' property
              const mapped = apiResponse.responsedata.assessments.map((assessment) => {
                let value = assessment.value
                  ? assessment.value.charAt(0).toUpperCase() + assessment.value.slice(1)
                  : assessment.value;
                if (value?.includes('Pass') && (!assessment.end_date || (assessment.end_date && new Date(assessment.end_date) < new Date()))) {
                  value = value ? value + ' (Expired)' : 'Expired';
                }
                const createdbyname = (assessment as any).createdbyname;
                let mappedCreatedByName = createdbyname;
                if (typeof createdbyname === 'string') {
                  if (createdbyname.includes('MH')) {
                    mappedCreatedByName = 'Mobile Health';
                  } else if (createdbyname.includes('IMA')) {
                    mappedCreatedByName = 'The IMA Group';
                  }
                }
                return {
                  ...assessment,
                  value,
                  createdbyname: mappedCreatedByName,
                };
              });
              this.healthAssessmentGridData = mapped;
            } else {
              this.healthAssessmentGridData = null;
            }
          },
          error: (err) => {
            throwError(() => this.errorHandler.handleError(err));
          },
        });
    }
  }

  setPendingRow() {
    this.healthAssessmentGridData = [{ ...pendingProviderHealthAssessment }];
  }

  private calculateHealthAssessmentDaysRemaining(): number | null {
    if (!this.healthAssessments) return null;
    const active = this.healthAssessments.find(item => item.is_active);
    if (!active?.end_date) return null;
    const endDate = new Date(active.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const diffMs = endDate.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  private toTitleCase(value: string | undefined | null): string {
    if (!value) return '';
    // Normalize: replace non-alphanumeric sequences with a single space, trim
    const words = value.replace(/[^a-zA-Z0-9]+/g, ' ').trim().split(/\s+/);
    if (words.length === 0) return '';
    // Title case each word: first letter uppercase, rest lowercase
    return words
      .map(w => {
        const lower = w.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

}
